import api from './api';

/**
 * RESERVATION-VISIBILITY.1a — read-only view of POS soft reservations for an
 * inventory item. Reservations are provisional holds POS creates when a line is
 * added to an order; they are NOT stock movements and never change on-hand
 * quantity. For recipe-backed items, holds live on the component ingredient
 * items (this returns holds where the queried item is the reserved item).
 */

export type ReservationStatus = 'ACTIVE' | 'RELEASED';

export interface InventoryItemReservation {
  id: string;
  locationId: string;
  quantity: number;
  status: ReservationStatus;
  sourceType: string;
  /** Opaque POS order reference (not a human table number). */
  sourceOrderRef: string;
  /** Opaque POS order-line reference. */
  sourceLineRef: string;
  /** 0-based component index for recipe-backed holds; null for direct items. */
  componentLineIndex: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface ItemReservationsResponse {
  /** ACTIVE reservations only, newest first. */
  reservations: InventoryItemReservation[];
  /** locationId → summed ACTIVE reserved quantity. */
  reservedByLocation: Record<string, number>;
  totalReserved: number;
}

export interface AgeInfo {
  formatted: string;
  ms: number;
  severity: 'normal' | 'amber' | 'red';
}

export interface HoldDetail {
  id: string;
  /** The POS order's identity — String(posOrder._id). Always present. */
  posRef: string;
  /**
   * POS-ORDER-NUMBER.1 — the human-readable order number, DISPLAY only.
   * Null on holds placed before the sender included one: fall back to posRef,
   * never to a blank cell.
   */
  posNumber: string | null;
  lineRef: string;
  componentIndex: number | null;
  locationName: string;
  qty: number;
  age: AgeInfo;
}

export interface AggregatedReservation {
  itemId: string;
  itemName: string;
  itemSize: string | null;
  itemSku: string | null;
  reservedQty: number;
  totalAvailable: number;
  orderCount: number;
  holdCount: number;
  locations: string[];
  oldestHoldAge: AgeInfo;
  holds: HoldDetail[];
}

export interface ActiveReservationsResponse {
  venueId: string;
  totalItemsReserved: number;
  totalHoldsStale: number;
  items: AggregatedReservation[];
}

const inventoryReservationService = {
  /** Active POS soft reservations for one inventory item (all locations). */
  getItemReservations: async (
    venueId: string,
    itemId: string,
  ): Promise<ItemReservationsResponse> => {
    const res = await api.get(`/admin/venue/${venueId}/inventory/item/${itemId}/reservations`);
    return res.data as ItemReservationsResponse;
  },

  /**
   * Fetch currently-active inventory reservations for a venue,
   * aggregated by item with per-item summary and inline-expandable detail.
   */
  getActiveReservations: async (venueId: string): Promise<ActiveReservationsResponse> => {
    const response = await api.get<ActiveReservationsResponse>(
      `/admin/venue/${venueId}/inventory/reservations/active`
    );
    return response.data;
  },
};

export default inventoryReservationService;
